import React, { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, ChatLeftText, ExclamationTriangleFill } from 'react-bootstrap-icons';
import Spinner from 'react-bootstrap/Spinner';

import { listOrganizationSupportTickets, replyToOrganizationSupportTicket, type SupportTicket } from '../api/support_tickets';
import './SuperAdminFeedback-new.css';
import './SuperAdminSupportTickets-new.css';

type TicketFilter = 'all' | 'open' | 'under_review' | 'refunds' | 'closed';

const FILTERS: Array<{ label: string; value: TicketFilter }> = [
    { label: 'All', value: 'all' },
    { label: 'Open', value: 'open' },
    { label: 'In review', value: 'under_review' },
    { label: 'Refunds', value: 'refunds' },
    { label: 'Closed', value: 'closed' },
];

function formatDate(value: string): string
{
    return new Date(value).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

function statusClass(status: string): string
{
    if (status === 'resolved' || status === 'refund_completed') return 'sst-status sst-status-success';
    if (status === 'rejected' || status === 'refund_failed') return 'sst-status sst-status-danger';
    if (status === 'refund_requested' || status === 'refund_processing') return 'sst-status sst-status-warning';
    return 'sst-status sst-status-info';
}

function isActionableTicket(ticket: SupportTicket): boolean
{
    return ['open', 'under_review'].includes(ticket.status);
}

export default function OrganizationSupportTickets(): React.ReactElement
{
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [selected, setSelected] = useState<SupportTicket | null>(null);
    const [filter, setFilter] = useState<TicketFilter>('all');
    const [reply, setReply] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () =>
    {
        setLoading(true);
        setError(null);
        try
        {
            const response = await listOrganizationSupportTickets(filter === 'all' ? undefined : filter);
            setTickets(response.items);
            setSelected((current) => response.items.find((ticket) => ticket.id === current?.id) || null);
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || 'Unable to load support tickets.');
        }
        finally
        {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { void load(); }, [load]);

    async function sendReply(): Promise<void>
    {
        if (!selected || !isActionableTicket(selected) || !reply.trim()) return;
        setSending(true);
        setError(null);
        try
        {
            const updated = await replyToOrganizationSupportTicket(selected.id, reply.trim());
            setSelected(updated);
            setReply('');
            await load();
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || 'Unable to send your reply.');
        }
        finally
        {
            setSending(false);
        }
    }

    return (
        <div className="saf-page sst-page">
            <header className="saf-page-header">
                <div>
                    <h1 className="saf-page-title">Support Tickets</h1>
                    <p className="saf-page-subtitle">Review billing charge concerns and replies from the AcceliQA support team.</p>
                </div>
                <button type="button" className="sst-refresh-button" onClick={() => void load()} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <ArrowClockwise size={15} />} Refresh
                </button>
            </header>

            <div className="sst-filter-bar" aria-label="Filter support tickets">
                <div className="saf-filter-switcher">
                    {FILTERS.map((item) => (
                        <button key={item.value} type="button" className={`saf-filter-pill ${filter === item.value ? 'saf-filter-pill-active' : ''}`} onClick={() => setFilter(item.value)}>
                            {item.label}
                        </button>
                    ))}
                </div>
                <span className="sst-count">{loading ? 'Loading tickets...' : `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`}</span>
            </div>

            {error && <div className="sst-alert"><ExclamationTriangleFill size={16} /> {error}</div>}

            <div className="sst-layout">
                <section className="sst-panel sst-queue" aria-label="Support tickets">
                    <div className="sst-panel-header"><div><h2>Your tickets</h2><p>{loading ? 'Loading...' : `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`}</p></div></div>
                    {loading ? <div className="sst-empty"><Spinner animation="border" size="sm" /> Loading tickets...</div> : tickets.length === 0 ? <div className="sst-empty">No support tickets match this filter.</div> : (
                        <div className="sst-ticket-list">
                            {tickets.map((ticket) => (
                                <button key={ticket.id} type="button" className={`sst-ticket ${selected?.id === ticket.id ? 'sst-ticket-selected' : ''}`} onClick={() => setSelected(ticket)}>
                                    <div className="sst-ticket-topline"><strong>{ticket.public_id}</strong><span className={statusClass(ticket.status)}>{ticket.status.replace(/_/g, ' ')}</span></div>
                                    <span className="sst-ticket-subject">{ticket.reason.replace(/_/g, ' ')}</span>
                                    <span className="sst-ticket-meta">{ticket.payment ? `$${(ticket.payment.amount_cents / 100).toFixed(2)} - ` : ''}Updated {formatDate(ticket.updated_at)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <section className="sst-panel sst-detail" aria-live="polite">
                    {!selected ? <div className="sst-empty sst-detail-empty"><ChatLeftText size={22} /><strong>Select a ticket</strong><span>The full conversation and ticket status will appear here.</span></div> : (
                        <>
                            <div className="sst-panel-header sst-detail-header"><div><div className="sst-ticket-topline"><h2>{selected.public_id}</h2><span className={statusClass(selected.status)}>{selected.status.replace(/_/g, ' ')}</span></div><p>Opened {formatDate(selected.created_at)}</p></div></div>
                            <div className="sst-detail-body">
                                <div className="sst-charge-summary"><div><span>Charge</span><strong>{selected.payment ? `$${(selected.payment.amount_cents / 100).toFixed(2)}` : 'Unavailable'}</strong></div><div><span>Payment type</span><strong>{(selected.payment?.payment_type || 'Billing payment').replace(/_/g, ' ')}</strong></div><div><span>Payment status</span><strong>{selected.payment?.status.replace(/_/g, ' ') || 'Unavailable'}</strong></div></div>
                                <div className="sst-section"><span className="sst-section-label">Issue reported</span><h3>{selected.reason.replace(/_/g, ' ')}</h3><p className="sst-description">{selected.description}</p></div>
                                <div className="sst-section"><span className="sst-section-label">Conversation</span><div className="sst-thread">{selected.messages.map((message) => <div key={message.id} className="sst-message"><p>{message.body}</p><span>{formatDate(message.created_at)}</span></div>)}</div>{isActionableTicket(selected) ? <><label className="sst-label" htmlFor="organization-support-reply">Reply to support</label><textarea id="organization-support-reply" className="sst-textarea" value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Add details that can help resolve this issue." disabled={sending} rows={4} /><button type="button" className="sst-button sst-button-primary" onClick={() => void sendReply()} disabled={!reply.trim() || sending}>{sending ? <><Spinner animation="border" size="sm" /> Sending...</> : <><ChatLeftText size={15} /> Send reply</>}</button></> : <p className="sst-readonly-notice">This ticket is closed and can no longer receive replies.</p>}</div>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
